import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, of, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketsEscalationRuleDrawerService } from '../../../services/tickets-config-drawer.services';
import { TicketEscalationRule, TicketPriority } from '../../../models/tickets-config.model';
import { getEscalationActionLabel, getEscalationTriggerLabel } from '../../../utils/ticket-format.util';

@Component({
  selector: 'app-tickets-escalation-rules-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './escalation-rules-list.component.html'
})
export class TicketsEscalationRulesListComponent implements OnInit, OnDestroy {
  items: TicketEscalationRule[] = [];
  filtered: TicketEscalationRule[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivas = false;

  /**
   * Inc 72: catálogo de prioridades para mapear el `priority` de la regla (que el
   * formulario guarda como el `level` de una prioridad) a su nombre legible.
   */
  priorities: TicketPriority[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private drawer: TicketsEscalationRuleDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.configApi
      .getPriorities()
      .pipe(catchError(() => of([] as TicketPriority[])), takeUntil(this.destroy$))
      .subscribe((data) => (this.priorities = data));
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  /**
   * Inc 72: nombre legible de la prioridad de la regla. El valor guardado es el
   * `level` de una prioridad (mismo criterio que el dropdown del formulario).
   * Las reglas antiguas guardan valores fuera del catálogo (10 del default del
   * form, 100 del default de la BD): se resuelven al `level` más cercano, el
   * MISMO criterio que `normalizePriorityControl()` del formulario, para que la
   * lista muestre exactamente la opción que el form preselecciona al abrirla.
   */
  triggerLabel(value: string | null | undefined): string {
    return getEscalationTriggerLabel(value);
  }

  actionLabel(value: string | null | undefined): string {
    return getEscalationActionLabel(value);
  }

  priorityLabel(priority: number | null | undefined): string {
    if (priority == null) return '—';
    const p = this.priorities.find((x) => x.level === priority)
      ?? this.closestPriority(priority);
    return (p && ((p.label && p.label.trim()) || p.name)) || String(priority);
  }

  private closestPriority(priority: number): TicketPriority | undefined {
    if (!this.priorities.length) return undefined;
    return this.priorities.reduce((a, b) =>
      Math.abs(b.level - priority) < Math.abs(a.level - priority) ? b : a
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getEscalationRules().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar reglas de escalación';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.items.filter((r) => {
      if (term && !r.name.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivas && !r.isActive) return false;
      return true;
    });
  }

  nuevo(): void { this.drawer.open(); }
  editar(item: TicketEscalationRule): void { this.drawer.open({ id: item.id }); }

  toggleActiva(item: TicketEscalationRule): void {
    this.configApi.updateEscalationRule(item.id, {
      name: item.name,
      description: item.description ?? undefined,
      triggerType: item.triggerType,
      triggerConfig: item.triggerConfig,
      actionType: item.actionType,
      actionConfig: item.actionConfig,
      isActive: !item.isActive,
      priority: item.priority
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.cargar(),
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo actualizar' })
    });
  }

  async eliminar(item: TicketEscalationRule): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar regla de escalación',
      message: `¿Eliminar la regla "${item.name}"?`
    });
    if (!ok) return;
    this.configApi.deleteEscalationRule(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.toast.deleted('Regla de escalación', `Se eliminó "${item.name}" correctamente.`); this.cargar(); },
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar' })
    });
  }

  volverATickets(): void { this.router.navigate(['/tickets']); }
}
